import { isPlatformSuperAdmin } from '../../auth'

type UserLike = Record<string, unknown> | null | undefined

export type AdminActorProfileOptions = {
  roleDetails?: {
    effectiveBehaviorType?: string | null
    baseRoleType?: string | null
    roleType?: string | null
  } | null
  tenant?: { effectiveBehaviorType?: string | null } | null
  auth?: unknown
}

function readRole(currentUser: UserLike): Record<string, unknown> | undefined {
  const role = currentUser?.role
  return role && typeof role === 'object' ? (role as Record<string, unknown>) : undefined
}

/** Canonical behavior for custom/system roles (matches backend getEffectiveBehaviorType). */
export function resolveEffectiveBehaviorType(
  currentUser: UserLike,
  opts?: AdminActorProfileOptions
): string {
  if (!currentUser) return ''
  const role = readRole(currentUser)
  const roleDetails = opts?.roleDetails
  const tenant = opts?.tenant

  const fromRoleDetails = roleDetails?.effectiveBehaviorType
  const fromTenant = tenant?.effectiveBehaviorType
  const fromRole = role?.effectiveBehaviorType
  const baseRoleType = role?.baseRoleType ?? roleDetails?.baseRoleType
  const roleNameOrType = role?.roleType ?? role?.roleName ?? roleDetails?.roleType ?? currentUser.roleType
  const roleNameStr = String(roleNameOrType ?? '').trim().toUpperCase()
  const baseStr = String(baseRoleType ?? '').trim().toUpperCase()

  if (fromRole) return String(fromRole).trim().toUpperCase()
  if (fromRoleDetails) return String(fromRoleDetails).trim().toUpperCase()
  if (fromTenant) return String(fromTenant).trim().toUpperCase()
  if (roleNameStr === 'SHORE') return 'COMPANY_GROUP_ADMIN'
  if (roleNameStr === 'CUSTOM' && baseStr) return baseStr
  if (roleNameStr && roleNameStr !== 'CUSTOM') return roleNameStr
  if (baseStr) return baseStr
  return ''
}

export function resolveActorCompanyGroupId(currentUser: UserLike): number | null {
  if (!currentUser) return null
  const vessel = currentUser.vessel as Record<string, unknown> | undefined
  const cg = currentUser.companyGroupAdmin as { id?: number } | undefined
  const fromVessel =
    vessel?.companyGroupAdminId ?? (vessel?.companyGroupAdmin as { id?: number } | undefined)?.id
  const n = Number(
    currentUser.companyGroupAdminId ?? cg?.id ?? fromVessel ?? currentUser.cgaid ?? 0
  )
  return Number.isFinite(n) && n > 0 ? n : null
}

export function isActorSuperAdmin(currentUser: UserLike, authOrOpts?: unknown): boolean {
  const opts: AdminActorProfileOptions | undefined =
    authOrOpts &&
    typeof authOrOpts === 'object' &&
    ('roleDetails' in (authOrOpts as object) || 'tenant' in (authOrOpts as object))
      ? (authOrOpts as AdminActorProfileOptions)
      : { auth: authOrOpts }
  if (isPlatformSuperAdmin(currentUser, opts?.auth ?? authOrOpts)) return true
  return resolveEffectiveBehaviorType(currentUser, opts) === 'SUPER_ADMIN'
}

export function isActorCompanyGroupAdmin(
  currentUser: UserLike,
  opts?: AdminActorProfileOptions
): boolean {
  return resolveEffectiveBehaviorType(currentUser, opts) === 'COMPANY_GROUP_ADMIN'
}

export function isActorCompanyAdmin(currentUser: UserLike, opts?: AdminActorProfileOptions): boolean {
  return resolveEffectiveBehaviorType(currentUser, opts) === 'COMPANY_ADMIN'
}

export function isActorOperator(currentUser: UserLike, opts?: AdminActorProfileOptions): boolean {
  return resolveEffectiveBehaviorType(currentUser, opts) === 'OPERATOR'
}

export function isEffectiveOperatorRole(
  value: unknown,
  opts?: AdminActorProfileOptions
): boolean {
  if (typeof value === 'string') {
    return value.trim().toUpperCase() === 'OPERATOR'
  }
  if (!value || typeof value !== 'object') {
    return false
  }
  const role = value as Record<string, unknown>
  const syntheticCurrentUser = {
    role: {
      roleType: role.roleType ?? role.roleBehaviorType,
      roleName: role.roleName,
      effectiveBehaviorType: role.effectiveBehaviorType,
      baseRoleType: role.baseRoleType,
      code: role.code,
    },
    roleType: role.roleType ?? role.roleBehaviorType,
  }
  return resolveEffectiveBehaviorType(syntheticCurrentUser, opts) === 'OPERATOR'
}

export function isEffectiveSuperintendentRole(
  value: unknown,
  opts?: AdminActorProfileOptions
): boolean {
  if (typeof value === 'string') {
    return value.trim().toUpperCase() === 'SUPERINTENDENT'
  }
  if (!value || typeof value !== 'object') {
    return false
  }
  const role = value as Record<string, unknown>
  const syntheticCurrentUser = {
    role: {
      roleType: role.roleType ?? role.roleBehaviorType,
      roleName: role.roleName,
      effectiveBehaviorType: role.effectiveBehaviorType,
      baseRoleType: role.baseRoleType,
      code: role.code,
    },
    roleType: role.roleType ?? role.roleBehaviorType,
  }
  return resolveEffectiveBehaviorType(syntheticCurrentUser, opts) === 'SUPERINTENDENT'
}

/** Platform/global operator (no company group binding). */
export function isActorGlobalScopeOperator(
  currentUser: UserLike,
  opts?: AdminActorProfileOptions
): boolean {
  return isActorOperator(currentUser, opts) && resolveActorCompanyGroupId(currentUser) == null
}

/** Operator bound to a company group (may manage that group's tenant). */
export function isActorScopedOperator(
  currentUser: UserLike,
  opts?: AdminActorProfileOptions
): boolean {
  return isActorOperator(currentUser, opts) && resolveActorCompanyGroupId(currentUser) != null
}

/** May pick any company group when creating users (superadmin or global operator). */
export function actorCanPickAnyCompanyGroup(
  currentUser: UserLike,
  opts?: AdminActorProfileOptions
): boolean {
  const auth = opts?.auth
  return isActorSuperAdmin(currentUser, opts ?? { auth }) || isActorGlobalScopeOperator(currentUser, opts)
}

/** Creates users scoped to own company group (CGA or company-scoped operator). */
export function actorUsesOwnCompanyGroupScope(
  currentUser: UserLike,
  opts?: AdminActorProfileOptions
): boolean {
  return isActorCompanyGroupAdmin(currentUser, opts) || isActorScopedOperator(currentUser, opts)
}

/** Effective CGA id for tenant-scoped admin actions. */
export function resolveEffectiveCompanyGroupId(currentUser: UserLike): number | null {
  const cgaFromProfile = resolveActorCompanyGroupId(currentUser)
  if (cgaFromProfile != null) return cgaFromProfile
  const fromEntity = Number(currentUser?.roleEntityId ?? 0)
  return Number.isFinite(fromEntity) && fromEntity > 0 ? fromEntity : null
}

/** Sub-company (CompanyAdmin) id for tenant-scoped company-admin actors. */
export function resolveActorCompanyAdminId(currentUser: UserLike): number | null {
  if (!currentUser) return null
  const fromProfile = Number(currentUser.companyAdminId ?? 0)
  if (Number.isFinite(fromProfile) && fromProfile > 0) return fromProfile
  const nested = currentUser.companyAdmin as { id?: number } | undefined
  const fromEntity = Number(currentUser.roleEntityId ?? nested?.id ?? 0)
  return Number.isFinite(fromEntity) && fromEntity > 0 ? fromEntity : null
}
